import React from "react";
import { useI18next } from "gatsby-plugin-react-i18next";
import { accentColor } from "../colors/colors";

export type SEOProps = {
  title: string;
  locales: {
    edges: Array<{
      node: {
        ns: string;
        data: string;
        language: string;
      };
    }>;
  };
  pathname?: string; // Optional pathname for hreflang tags
};

const SEO: React.FC<SEOProps> = ({ title, locales, pathname = "/" }) => {
  // Get current language from i18n context (most reliable source)
  const { language } = useI18next();
  const currentLanguage = language || "fr";
  const locale = currentLanguage === "en" ? "en_US" : "fr_FR";

  const commonData = JSON.parse(
    locales.edges.find((edge) => edge.node.ns === "common")?.node?.data as any
  );
  const contactData = JSON.parse(
    locales.edges.find((edge) => edge.node.ns === "contact")?.node?.data as any
  );

  // SEO metadata with fallbacks
  const siteTitle = title || commonData["fullName"];
  const siteDescription = commonData["meta.description"];
  const siteUrl = commonData["website"];
  const siteImage = `${siteUrl}/images/slider/1.jpg`;
  const siteLogo = `${siteUrl}/images/icon.png`; // Logo officiel de l'hôtel
  const businessPhone = contactData["phone.label"]; // International format: +33 5 56 52 24 62
  const businessEmail = contactData["email.address"];
  const businessAddress = contactData["address"];

  // Parse address components for structured data
  // Address format: "82 Rue du Palais Gallien, 33000 Bordeaux, France"
  const addressParts = businessAddress.split(", ");
  const streetAddress = addressParts[0] || "82 Rue du Palais Gallien";
  const postalAndCity = addressParts[1] || "33000 Bordeaux";
  const postalCode = postalAndCity.split(" ")[0] || "33000";
  const city = postalAndCity.split(" ").slice(1).join(" ") || "Bordeaux";
  const country = addressParts[2] || "France";

  // Keywords for SEO with fallbacks
  const keywords = [
    commonData["meta.keyword1"],
    commonData["meta.keyword2"],
    commonData["meta.keyword3"],
    commonData["meta.keyword4"],
    commonData["meta.keyword5"],
    commonData["meta.keyword6"],
    commonData["meta.keyword7"],
    commonData["meta.keyword8"],
    commonData["meta.keyword9"],
    commonData["meta.keyword10"],
    commonData["meta.keyword11"],
    commonData["meta.keyword12"],
    commonData["meta.keyword13"],
  ].join(", ");

  // Generate hreflang URLs for multilingual SEO
  // Remove /en prefix if present to get the base path
  const basePath = pathname.replace(/^\/en/, "") || "/";
  const frenchUrl = `${siteUrl}${basePath}`;
  const englishUrl = `${siteUrl}/en${basePath}`;
  const defaultUrl = frenchUrl; // French is the default language

  // JSON-LD structured data for search engines
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Hotel",
        "@id": `${siteUrl}/#hotel`,
        name: siteTitle,
        description: siteDescription,
        url: siteUrl,
        logo: siteLogo,
        image: siteImage,
        telephone: businessPhone,
        email: businessEmail,
        address: {
          "@type": "PostalAddress",
          streetAddress: streetAddress,
          addressLocality: city,
          postalCode: postalCode,
          addressCountry: "FR",
        },
        geo: {
          "@type": "GeoCoordinates",
          latitude: "44.8578",
          longitude: "-0.5787",
        },
        priceRange: "€150-€250",
        starRating: {
          "@type": "Rating",
          ratingValue: "4",
        },
        amenityFeature: [
          {
            "@type": "LocationFeatureSpecification",
            name: "Free WiFi",
            value: true,
          },
          {
            "@type": "LocationFeatureSpecification",
            name: "Air conditioning",
            value: true,
          },
          {
            "@type": "LocationFeatureSpecification",
            name: "Safe",
            value: true,
          },
          {
            "@type": "LocationFeatureSpecification",
            name: "Parking",
            value: true,
          },
        ],
        sameAs: [
          "https://www.tripadvisor.fr/Hotel_Review-g187079-d12258612-Reviews-Hotel_Churchill_Bordeaux_Centre-Bordeaux_Gironde_Nouvelle_Aquitaine.html",
          "https://www.booking.com/hotel/fr/le-dauphin.fr.html",
          "https://www.trivago.fr/fr/oar/h%C3%B4tel-churchill-bordeaux-centre?search=100-1045764",
          "https://maps.app.goo.gl/vU3YjKwU9pxgveLS7",
        ],
      },
      {
        "@type": "WebSite",
        "@id": `${siteUrl}/#website`,
        url: siteUrl,
        name: siteTitle,
        description: siteDescription,
        publisher: {
          "@id": `${siteUrl}/#hotel`,
        },
      },
    ],
  };

  return (
    <>
      <meta charSet="utf-8" />
      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />

      {/* Primary Meta Tags */}
      <title>{siteTitle}</title>
      <meta name="title" content={siteTitle} />
      <meta name="description" content={siteDescription} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={siteTitle} />
      <meta name="robots" content="index, follow" />
      <meta name="language" content="en" />
      <meta name="revisit-after" content="7 days" />

      {/* Canonical URL - For multilingual SEO best practices:
          - Default language (French) pages have self-referential canonical
          - Alternate language (English) pages point canonical to French version
          - This tells Google that French is the primary content */}
      <link
        rel="canonical"
        href={currentLanguage === "en" ? frenchUrl : `${siteUrl}${pathname}`}
      />

      {/* hreflang tags for multilingual SEO */}
      <link rel="alternate" hrefLang="fr" href={frenchUrl} />
      <link rel="alternate" hrefLang="en" href={englishUrl} />
      <link rel="alternate" hrefLang="x-default" href={defaultUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={`${siteUrl}${pathname}`} />
      <meta property="og:title" content={siteTitle} />
      <meta property="og:description" content={siteDescription} />
      <meta property="og:image" content={siteImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={siteTitle} />
      <meta property="og:locale" content={locale} />
      <meta
        property="business:contact_data:street_address"
        content={streetAddress}
      />
      <meta property="business:contact_data:locality" content={city} />
      <meta property="business:contact_data:postal_code" content={postalCode} />
      <meta property="business:contact_data:country_name" content={country} />
      <meta
        property="business:contact_data:phone_number"
        content={businessPhone}
      />
      <meta property="business:contact_data:email" content={businessEmail} />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={`${siteUrl}${pathname}`} />
      <meta property="twitter:title" content={siteTitle} />
      <meta property="twitter:description" content={siteDescription} />
      <meta property="twitter:image" content={siteImage} />

      {/* Additional SEO Meta Tags */}
      <meta name="theme-color" content={accentColor} />
      <meta name="msapplication-TileColor" content={accentColor} />
      <meta name="application-name" content={siteTitle} />
      <meta name="apple-mobile-web-app-title" content={siteTitle} />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="mobile-web-app-capable" content="yes" />

      {/* Geo Tags */}
      <meta name="geo.region" content="FR-33" />
      <meta name="geo.placename" content={city} />
      <meta name="geo.position" content="44.8578;-0.5787" />
      <meta name="ICBM" content="44.8578, -0.5787" />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>


    </>
  );
};

export default SEO;
